export function format(txt, column, record) {
  if (record.highlight === undefined || record.highlight === null) {
    return txt;
  }
  const pattern = /<em>([\s\S]*?)<\/em>/g;
  const highlightTexts = record.highlight["_d_"][column];
  let formattedTxt = txt;
  if (highlightTexts) {
    const highlightText = highlightTexts.join(" ");
    let hwords = [];
    try {
      hwords = highlightText
        .match(pattern)
        .map((h) => h.replace("<em>", "").replace("</em>", ""));
    } catch (e) {
      console.log(e);
    }
    hwords.forEach((word) => {
      formattedTxt = formattedTxt.replaceAll(
        word,
        `<div class="___ndhighlighter0x2824___">${word}</div>`
      );
    });
  }
  return formattedTxt;
}

// Several report fields (authors, project_name, company_name) come back from the
// API as lists. Rendering a list directly concatenates the items with no separator.
export function formatList(value, separator = ", ") {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(separator);
  }
  return value || "";
}

// project_name is a list of name variants for the same project, e.g.
// ["Shea Creek Project", "Shea Creek"]. Every surface that shows a project name
// — cards, detail header, map popups, chat results — goes through this so they
// all agree on which variant is the display name.
export function primaryName(value, fallback = "") {
  if (Array.isArray(value)) {
    const names = value.filter(Boolean);
    return names[names.length - 1] || fallback;
  }
  return value || fallback;
}

export const fields = [
  { label: "Title", value: "title" },
  { label: "Summary", value: "summary" },
  { label: "Article Text", value: "content" },
];

// TODO Automate this!
export const sources = [
  { label: "Mining.com", value: "mining", labelColor: "green" },
  { label: "The Northern Miner", value: "northernminer", labelColor: "blue" },
  {
    label: "Junior Mining Network",
    value: "juniorminingnetwork",
    labelColor: "red",
  },
  { label: "Australian Mining", value: "australianmining", labelColor: "teal" },
  { label: "ASX", value: "asx", labelColor: "yellow" },
  { label: "TSX (via Stockhouse)", value: "tsx", labelColor: "pink" },
  { label: "Miningnews.net", value: "miningnews", labelColor: "purple" },
  { label: "LSX", value: "lsx", labelColor: "orange" },
  { label: "Rumbominero", value: "rumbominero", labelColor: "cyan" },
  {
    label: "Noticiasdemineracao",
    value: "noticiasdemineracao",
    labelColor: "aqua",
  },
  {
    label: "GBReports",
    value: "gbreports",
  },
  {
    label: "Galileo Resources",
    value: "galileoresources",
  },
  {
    label: "Extractor Magzine",
    value: "extractormagzine",
  },
  {
    label: "Copperbelt Katanga Mining",
    value: "copperbeltkatangamining",
  },
  {
    label: "African Pioneer",
    value: "africanpioneerplc",
  },
  {
    label: "AfriExploreNewsletter",
    value: "afriexplorenewsletter",
  },
];

const colorCodes = {};

sources.forEach((s) => {
  colorCodes[s.value] = s.labelColor;
});

export { colorCodes };

// A section's (and a table's) `page` arrives as a list of page numbers. The list
// is often unsorted and sometimes has gaps, e.g. [32, 34, 30, 31]. Normalise it
// to ascending, de-duplicated numbers.
export function pageNumbers(page) {
  const list = Array.isArray(page) ? page : [page];
  const nums = list
    .filter((p) => p !== null && p !== undefined && p !== "")
    .map(Number)
    .filter(Number.isFinite);
  return [...new Set(nums)].sort((a, b) => a - b);
}

// Label those pages the way a reader expects, collapsing consecutive runs:
// [41] -> "Page 41", [20, 21] -> "Pages 20-21", [3, 4, 7, 8, 9, 10] -> "Pages 3-4, 7-10"
export function formatPages(page) {
  const nums = pageNumbers(page);
  if (nums.length === 0) return "";

  const ranges = [];
  let start = nums[0];
  let end = nums[0];
  for (const n of nums.slice(1)) {
    if (n === end + 1) {
      end = n;
    } else {
      ranges.push(start === end ? `${start}` : `${start}–${end}`);
      start = n;
      end = n;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);

  return `${nums.length === 1 ? "Page" : "Pages"} ${ranges.join(", ")}`;
}

// `provenance` arrives as a JSON string keyed by report attribute, each value a
// list of evidence entries ({ quote, match, page, ...attribute-specific fields }).
// Returns {} for anything unparseable so a bad payload can never break the page.
export function parseProvenance(provenance) {
  if (!provenance) return {};

  let parsed = provenance;
  if (typeof provenance === "string") {
    try {
      parsed = JSON.parse(provenance);
    } catch (e) {
      console.warn("Could not parse provenance", e);
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

  const byAttribute = {};
  for (const [attribute, value] of Object.entries(parsed)) {
    const entries = (Array.isArray(value) ? value : [value]).filter(
      (entry) => entry && typeof entry === "object" && !Array.isArray(entry)
    );
    if (entries.length > 0) byAttribute[attribute] = entries;
  }
  return byAttribute;
}

// `location_coordinates` is a GeoJSON-ordered [longitude, latitude] pair.
export function formatCoordinates(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return "";
  const [longitude, latitude] = coordinates.map(Number);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";

  const axis = (value, positive, negative) =>
    `${Math.abs(value).toFixed(5)}\u00b0 ${value >= 0 ? positive : negative}`;

  return `${axis(latitude, "N", "S")}, ${axis(longitude, "E", "W")}`;
}
